/** @module Number and money formatting; the locale decides, never the component. */

export function money(value: string | number | null | undefined, locale: string): string | null {
  if (value === null || value === undefined || value === "") return null;
  const amount = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(amount)) return null;
  return new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(amount);
}

/** "+4,00 €" or "−4,50 €": a gain or loss always carries its sign. */
export function signedMoney(
  value: string | number | null | undefined,
  locale: string,
): string | null {
  const plain = money(
    value === null || value === undefined ? value : Math.abs(Number(value)),
    locale,
  );
  if (plain === null) return null;
  return `${Number(value) < 0 ? "−" : "+"}${plain}`;
}

/** An API amount ("6.00") as the user would type it ("6,00"); empty when there is none. */
export function amountText(value: string | null | undefined, locale: string): string {
  if (value === null || value === undefined || value === "") return "";
  return Number(value).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}

/**
 * A typed amount for the API: "2,50", "2.50" and "1.234,50" all work.
 *
 * Returns null for an empty field and undefined for text that is no amount.
 */
export function parseAmount(text: string): string | null | undefined {
  const cleaned = text.replace(/[\s€]/g, "");
  if (!cleaned) return null;
  const last = Math.max(cleaned.lastIndexOf(","), cleaned.lastIndexOf("."));
  let normal = cleaned;
  if (last >= 0) {
    const whole = cleaned.slice(0, last).replace(/[.,]/g, "");
    const fraction = cleaned.slice(last + 1);
    // Three digits after the last mark are thousands ("1.234"), never cents.
    normal = fraction.length === 3 ? `${whole}${fraction}` : `${whole}.${fraction}`;
  }
  if (!/^\d{1,10}(\.\d{1,2})?$/.test(normal)) return undefined;
  return Number(normal).toFixed(2);
}

export function count(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

const UNITS = ["B", "kB", "MB", "GB", "TB"] as const;

/** Bytes as a short, readable size; 1000-based, like every disk vendor counts. */
export function bytes(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "–";
  let size = Math.max(0, value);
  let unit = 0;
  while (size >= 1000 && unit < UNITS.length - 1) {
    size /= 1000;
    unit += 1;
  }
  const digits = size < 10 && unit > 0 ? 1 : 0;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(size)} ${UNITS[unit]}`;
}

/** A span of seconds as days, hours and minutes; only the two largest parts. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return "–";
  const total = Math.max(0, Math.round(seconds));
  const parts: string[] = [];
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days) parts.push(`${days} d`);
  if (hours) parts.push(`${hours} h`);
  if (minutes || parts.length === 0) parts.push(`${minutes} min`);
  return parts.slice(0, 2).join(" ");
}

/** Today in the reader's own time zone as "2026-10-06", the value of a date field. */
export function todayIso(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const dayOfMonth = String(now.getDate()).padStart(2, "0");
  return `${String(now.getFullYear())}-${month}-${dayOfMonth}`;
}

/** "19.09.2026" in German, the reader's own order elsewhere. */
export function day(value: string, locale: string): string {
  return new Date(value).toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** "19.09." for chart axes, where the year is obvious. */
export function dayMonth(value: string, locale: string): string {
  return new Date(value).toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });
}

/** "19.09.2026, 04:00": when a price was last updated. */
export function dateTime(value: string, locale: string): string {
  return new Date(value).toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
